/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { Color } from '@color';
import { fixedNumber } from '@utils';
import { optimize, type Config } from 'svgo';
import formatXml from 'xml-formatter';
import { lineSegment, point } from './constructors';
import type { LineSegment } from './LineSegment';
import { Point } from './Point';
import { Rect } from './Rect';

export function makeSvg(bounds: Rect, content: string, fractionDigits?: number): string {
  return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${fixedNumber(bounds.width, fractionDigits)}" height="${fixedNumber(bounds.height, fractionDigits)}" viewBox="${fixedNumber(bounds.left, fractionDigits)} ${fixedNumber(bounds.top, fractionDigits)} ${fixedNumber(bounds.width, fractionDigits)} ${fixedNumber(bounds.height, fractionDigits)}">
          <rect x="${fixedNumber(bounds.left, fractionDigits)}" y="${fixedNumber(bounds.top, fractionDigits)}" width="${fixedNumber(bounds.width, fractionDigits)}" height="${fixedNumber(bounds.height, fractionDigits)}" fill="white"/>
          ${svgGrid(bounds)}
          ${content}
      </svg>
  `.trim();
}

export function svgLine(
  line: LineSegment,
  strokeColor: Color,
  strokeWidth: number,
  fractionDigits?: number
): string {
  return `<line x1="${fixedNumber(line.start.x, fractionDigits)}" y1="${fixedNumber(line.start.y, fractionDigits)}" x2="${fixedNumber(line.end.x, fractionDigits)}" y2="${fixedNumber(line.end.y, fractionDigits)}" stroke="${strokeColor.toCSS()}" stroke-width="${fixedNumber(strokeWidth, fractionDigits)}"/>`;
}

export function svgCircle(
  center: Point,
  radius: number,
  fillColor: Color,
  fractionDigits?: number
): string {
  return `<circle cx="${fixedNumber(center.x, fractionDigits)}" cy="${fixedNumber(center.y, fractionDigits)}" r="${fixedNumber(radius, fractionDigits)}" fill="${fillColor.toCSS()}"/>`;
}

export function svgRect(
  rect: Rect,
  strokeColor: Color,
  strokeWidth: number,
  fractionDigits?: number
): string {
  return `<rect x="${fixedNumber(rect.left, fractionDigits)}" y="${fixedNumber(rect.top, fractionDigits)}" width="${fixedNumber(rect.width, fractionDigits)}" height="${fixedNumber(rect.height, fractionDigits)}" fill="none" stroke="${strokeColor.toCSS()}" stroke-width="${fixedNumber(strokeWidth, fractionDigits)}"/>`;
}

export function svgPath(
  pathData: string,
  strokeColor: Color,
  strokeWidth: number,
  fractionDigits?: number
): string {
  return `<path d="${pathData}" fill="none" stroke="${strokeColor.toCSS()}" stroke-width="${fixedNumber(strokeWidth, fractionDigits)}"/>`;
}

export function svgGrid(bounds: Rect, fractionDigits?: number): string {
  const gridSpacing = 10;
  const originX = 0;
  const originY = 0;
  const minX = bounds.left;
  const minY = bounds.top;
  const maxX = bounds.right;
  const maxY = bounds.bottom;

  let gridLines = '';
  for (
    let x = Math.round(minX / gridSpacing) * gridSpacing;
    x <= maxX;
    x += gridSpacing
  ) {
    const color = x === originX ? new Color(0, 0, 0) : new Color(0.8, 0.8, 0.8);
    const strokeWidth = x === originX ? 1 : 0.5;
    gridLines += svgLine(
      lineSegment(point(x, minY), point(x, maxY)),
      color,
      strokeWidth,
      fractionDigits
    );
  }
  for (
    let y = Math.round(minY / gridSpacing) * gridSpacing;
    y <= maxY;
    y += gridSpacing
  ) {
    const color = y === originY ? new Color(0, 0, 0) : new Color(0.8, 0.8, 0.8);
    const strokeWidth = y === originY ? 1 : 0.5;
    gridLines += svgLine(
      lineSegment(point(minX, y), point(maxX, y)),
      color,
      strokeWidth,
      fractionDigits
    );
  }

  return gridLines;
}

export function beautifySvg(svg: string): string {
  return formatXml(svg, {
    collapseContent: true,
    lineSeparator: '\n',
    indentation: '  '
  });
}

export function optimizeSvg(svg: string): string {
  // When optimizing we keep `<title>` elements because they are useful for
  // accessibility (e.g. hover tooltips), and may be used by search and index
  // functions, but we remove `<desc>` elements because they are primarily
  // useful for forensics.
  const config: Config = {
    plugins: [
      {
        name: 'preset-default',
        params: {
          overrides: {
            removeTitle: false,
            removeDesc: {
              removeAny: true
            }
          }
        }
      }
    ]
  };
  return optimize(svg, config).data;
}

export enum SvgOutputMode {
  NORMAL,
  BEAUTIFIED,
  OPTIMIZED
}

export function tokenizeSvgPathData(path: string): (string | number)[] {
  const commandRegex = /[a-zA-Z]/;
  const numberRegex = /-?\d*\.?\d+(?:[eE][-+]?\d+)?/g;

  const tokens: (string | number)[] = [];
  let index = 0;

  while (index < path.length) {
    const char = path[index];

    if (/\s|,/.test(char)) {
      // Skip over whitespace and commas
      index++;
    } else if (commandRegex.test(char)) {
      // It's a command like M, C, L, A, etc.
      tokens.push(char);
      index++;
    } else {
      // It must be a number, so extract it
      numberRegex.lastIndex = index;
      const numberMatch = numberRegex.exec(path);

      if (numberMatch) {
        tokens.push(parseFloat(numberMatch[0]));
        index = numberRegex.lastIndex;
      } else {
        throw new Error(
          `Unexpected character at position ${index.toString()}: '${char}'`
        );
      }
    }
  }

  return tokens;
}

export function composeSvgPathData(
  components: (string | number)[],
  fractionDigits?: number
): string {
  let result = '';
  let lastWasNumber = false;
  for (const component of components) {
    if (typeof component === 'number') {
      if (lastWasNumber) {
        result += ' ';
      }
      result += fixedNumber(component, fractionDigits);
      lastWasNumber = true;
    } else if (typeof component === 'string') {
      result += component;
      lastWasNumber = false;
    }
  }
  return result;
}

/**
 * Removes the outer <svg> wrapper and prefixes every class name (both in
 * attributes and inside <style>) with the supplied string.
 */
export function stripRootAndPrefixClasses(svg: string, prefix: string): string {
  // We write the plug‑in inline and then cast it to Config['plugins'][number]
  // so the presence of the undocumented `type` key doesn’t break type‑checking.
  const stripAndPrefix = {
    name: 'strip-root-and-prefix-classes',
    // ⚠️  undocumented but supported by SVGO’s runtime
    type: 'full',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn(root: any) {
      // unwrap the single top‑level <svg>, if present
      if (
        root.children?.length === 1 &&
        root.children[0].type === 'element' &&
        root.children[0].name === 'svg'
      ) {
        root.children = root.children[0].children ?? [];
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const walk = (node: any) => {
        if (node.type === 'element' && node.attributes?.class) {
          node.attributes.class = node.attributes.class
            .split(/\s+/)
            .map((c: string) => `${prefix}-${c}`)
            .join(' ');
        }
        if (node.type === 'element' && node.name === 'style') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          node.children?.forEach((child: any) => {
            if (child.type === 'text' && typeof child.value === 'string') {
              child.value = child.value.replace(
                /\.([A-Za-z0-9_-]+)/g,
                (_: string, cls: string) => `.${prefix}-${cls}`
              );
            }
          });
        }
        node.children?.forEach(walk);
      };

      root.children?.forEach(walk);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return root;
    }
  };

  return optimize(svg, { plugins: [stripAndPrefix] }).data;
}
